import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Form, FormRow } from '@/components/Form'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import styles from './FinalReturnAmountsDialog.module.css'

export interface FinalReturnAmountsDialogProps {
  showing: boolean
  onClose: () => unknown
  onAddYear: () => unknown
  onSubmit: () => unknown
  isSubmitPending: boolean
  isSubmitDisabled: boolean
  submitLabel?: string
  /**
   * One `FinalReturnAmountsYearSection` per tax year.
   */
  children?: ReactNode
}

export function FinalReturnAmountsDialog({
  showing,
  onClose,
  onAddYear,
  onSubmit,
  isSubmitPending,
  isSubmitDisabled,
  submitLabel = 'Save & Mark Filed',
  children,
}: FinalReturnAmountsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Final Return Amounts</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <div
            className={styles.signHint}
            data-test="final-return-amounts-hint"
          >
            Enter amounts as: Refund (+) / Owed (−)
          </div>
          <Form>
            {children}

            <FormRow>
              <Button
                label="Add Year"
                variant="secondary"
                size="wide-slim"
                onPress={onAddYear}
                testId="final-return-amounts-add-year"
              />
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />
          <Button
            label={submitLabel}
            variant="primary"
            testId="final-return-amounts-submit-button"
            isPending={isSubmitPending}
            isDisabled={isSubmitDisabled}
            onPress={onSubmit}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface FinalReturnAmountsYearSectionProps {
  index: number
  taxYearInput: ReactNode
  federalAmountInput: ReactNode
  onRemove: () => unknown
  onAddState: () => unknown
  /**
   * One `FinalReturnAmountsStateEntry` per state.
   */
  children?: ReactNode
}

export function FinalReturnAmountsYearSection({
  index,
  taxYearInput,
  federalAmountInput,
  onRemove,
  onAddState,
  children,
}: FinalReturnAmountsYearSectionProps) {
  return (
    <div
      className={styles.yearBlock}
      data-test={`final-return-amounts-year-${index}`}
    >
      <div className={styles.yearHeader}>
        <div className={styles.yearHeaderField}>{taxYearInput}</div>
        <div className={styles.yearHeaderField}>{federalAmountInput}</div>
        <div className={styles.stateEntryRemove}>
          <IconButton
            name="close"
            label="Remove year"
            onPress={onRemove}
            testId={`final-return-amounts-remove-year-${index}`}
          />
        </div>
      </div>

      {children}

      <FormRow>
        <Button
          label="Add State"
          variant="secondary"
          size="wide-slim"
          onPress={onAddState}
          testId={`final-return-amounts-add-state-${index}`}
        />
      </FormRow>
    </div>
  )
}

export interface FinalReturnAmountsStateEntryProps {
  yearIndex: number
  index: number
  stateInput: ReactNode
  amountInput: ReactNode
  onRemove: () => unknown
}

export function FinalReturnAmountsStateEntry({
  yearIndex,
  index,
  stateInput,
  amountInput,
  onRemove,
}: FinalReturnAmountsStateEntryProps) {
  return (
    <FormRow>
      <div className={styles.stateEntryRow}>
        <div className={styles.stateEntryField}>{stateInput}</div>
        <div className={styles.stateEntryField}>{amountInput}</div>
        <div className={styles.stateEntryRemove}>
          <IconButton
            name="close"
            label="Remove state"
            onPress={onRemove}
            testId={`final-return-amounts-remove-state-${yearIndex}-${index}`}
          />
        </div>
      </div>
    </FormRow>
  )
}
